import "./index.less";
import * as React from "react";
import { AcceptedValueType, reactSelectIconStyles, reactSelectStyles } from "../AtAutoCompleteSelectField";
import { CheckMarkOption } from "../AtAutoCompleteSelectField/components/CheckMarkOption";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormGroup, FormText, Label } from "reactstrap";
import { FormatOptionLabelMeta } from "react-select/src/Select";
import { IconMultiValueRemove } from "../AtAutoCompleteSelectField/components/IconMultiValueRemove";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { MaxItemsValueContainer } from "../AtAutoCompleteSelectField/components/MaxItemsValueContainer";
import { OptionsType, ValueType } from "react-select/src/types";
import { components as ReactSelectComponents } from "react-select";
import { SelectComponentsConfig } from "react-select/src/components";
import { StylesConfig } from "react-select/src/styles";
import { inject, observer } from "mobx-react";
import Async from "react-select/async";
import BadgeMultiValueContainer from "../AtAutoCompleteSelectField/components/BadgeMultiValueContainer";

export interface IAtAsyncAutoCompleteSelectFieldProps<TValue> {
    fieldName?: string;
    label?: string;
    loadOptions: (inputValue: string, callback: (options: OptionsType<TValue>) => void) => Promise<any> | void;
    value: AcceptedValueType<TValue>;
    // @ts-ignore
    onChange: (option: ValueType<AcceptedValueType<TValue>>, fieldName?: string) => void;
    cacheOptions?: boolean;
    defaultOptions?: OptionsType<TValue> | boolean;
    placeholder?: string;
    formText?: string;
    isMulti?: boolean;
    hideSelectedOptions?: boolean;
    closeMenuOnSelect?: boolean;
    formatOptionLabel?: (data: TValue, context: FormatOptionLabelMeta<TValue>) => React.ReactNode;
    icon?: IconProp;
    isClearable?: boolean;
    isDisabled?: boolean;
    additionalStyles?: StylesConfig;
    selectRef?: React.RefObject<Async<any>>;
    selectKey?: string;

    maxNumberOfValuesToDisplay?: number;
}

@inject()
@observer
export class AtAsyncAutoCompleteSelectField<TValue> extends React.Component<
    IAtAsyncAutoCompleteSelectFieldProps<TValue>,
    {}
> {
    onChangeHandler = (option) => {
        const { onChange, fieldName } = this.props;

        return onChange(option, fieldName);
    };

    render() {
        const {
            loadOptions,
            cacheOptions,
            defaultOptions,
            value,
            placeholder,
            formText,
            selectRef,
            label,
            formatOptionLabel,
            icon,
            isMulti = false,
            hideSelectedOptions = false,
            closeMenuOnSelect = true,
            isClearable = false,
            isDisabled = false,
            selectKey,
            maxNumberOfValuesToDisplay = 1,
            additionalStyles = { container: (styles) => ({}), control: (styles) => ({}) } as StylesConfig,
        } = this.props;

        const ValueContainer = (props) => {
            return (
                <ReactSelectComponents.ValueContainer {...props}>
                    {icon ? <FontAwesomeIcon icon={icon} style={{ marginRight: 12 }} /> : null}
                    {props.children}
                </ReactSelectComponents.ValueContainer>
            );
        };

        const defaultStyles: StylesConfig = icon ? reactSelectIconStyles : reactSelectStyles;
        const customStyles: StylesConfig = {
            ...defaultStyles,
            ...additionalStyles,
            container: (styles, state) => ({
                ...styles,
                ...(additionalStyles.container ? additionalStyles.container(styles, state) : {}),
                ...(defaultStyles.container ? defaultStyles.container(styles, state) : {}),
            }),
            control: (styles, state) => ({
                ...styles,
                ...(additionalStyles.control ? additionalStyles.control(styles, state) : {}),
                ...(defaultStyles.control ? defaultStyles.control(styles, state) : {}),
            }),
        };

        let selectComponents: SelectComponentsConfig<TValue> = {};

        if (icon) {
            selectComponents = {
                ...selectComponents,
                ValueContainer,
            };
        }

        if (isMulti && maxNumberOfValuesToDisplay) {
            selectComponents = {
                ValueContainer: (props) => (
                    // @ts-ignore
                    <MaxItemsValueContainer {...props} maxNumberOfValuesToDisplay={maxNumberOfValuesToDisplay} />
                ),
                // @ts-ignore
                Option: CheckMarkOption,
                MultiValueContainer: BadgeMultiValueContainer,
                MultiValueRemove: IconMultiValueRemove,
            };
        }

        return (
            <FormGroup className="AtAsyncAutoCompleteSelectField">
                {label ? <Label>{label}</Label> : null}
                <form autoComplete={"none"}>
                    <Async
                        loadOptions={loadOptions}
                        value={value}
                        onChange={this.onChangeHandler}
                        styles={customStyles}
                        placeholder={placeholder}
                        isMulti={isMulti}
                        cacheOptions={cacheOptions}
                        defaultOptions={defaultOptions}
                        closeMenuOnSelect={closeMenuOnSelect}
                        hideSelectedOptions={hideSelectedOptions}
                        formatOptionLabel={formatOptionLabel}
                        noOptionsMessage={({ inputValue }) => "global.noOptions".localize()}
                        loadingMessage={({ inputValue }) => "global.loading".localize()}
                        isClearable={isClearable}
                        isDisabled={isDisabled}
                        components={selectComponents}
                        ref={selectRef}
                        key={selectKey}
                    />
                </form>
                {formText ? <FormText>{formText}</FormText> : null}
            </FormGroup>
        );
    }
}
